<template>
    <Card class="settings-import">
        <preloader :display="loading" />
        <h2 class="typographies-heading-h2">{{ $l10n.__('Nouvel import') }}</h2>
        <CompassAlert
            v-if="!isImportUnlimited"
            :showClose="false"
            type="warning"
            :description="textAlertLimitedImport"
        />
        <p class="typographies-paragraph-medium-regular">
            {{
                $l10n.__(
                    'Ajoutez vos données à Sellsy pour la première fois ou mettez les à jour selon vos besoins. Pour vous faciliter ce processus, vous garantir une fiabilité des données importées et vous faire gagner en productivité, vous bénéficiez de fonctionnalités comme la gestion des erreurs ou la gestion des doublons.',
                )
            }}
        </p>
        <p class="documentation-link typographies-paragraph-medium-semibold">
            {{ $l10n.__('Besoin d’aide ?') }}
            <CompassLink
                link="https://help.sellsy.com/fr/?q=import"
                target="blank"
                :label="$t.__('common.actions.view_import_data_articles')"
            />
        </p>
        <div class="start-import-block">
            <div class="information-block">
                <IconImport />
                <div>
                    <h3 class="typographies-heading-h3">
                        {{ $l10n.__('Importer ou mettre à jour vos données dans Sellsy') }}
                    </h3>
                    <p class="typographies-paragraph-medium-regular">
                        {{
                            $l10n.__(
                                'Importez simplement vos données depuis une feuille de calcul pour retrouver rapidement vos sociétés, particuliers, contacts, commentaires, produits ou services !',
                            )
                        }}
                    </p>
                </div>
            </div>
            <CompassButton
                :label="$t.__('settings.import.SettingsImportComponent.start_import')"
                @click="showDialog = true"
            />
        </div>
        <h4 class="typographies-heading-h4">
            {{ $l10n.__('Vos imports rapide, facile et sans risque !') }}
        </h4>
        <div class="advantages-block-container">
            <div class="advantages-block">
                <div>
                    <CompassIcon icon="flash" size="large" />
                </div>
                <div>
                    <h6 class="typographies-heading-h6">
                        {{ $l10n.__('Soyez prêts rapidement') }}
                    </h6>
                    <p class="typographies-paragraph-small-regular">
                        {{
                            $l10n.__(
                                'Nous savons que votre temps est précieux, importez vos données facilement dans Sellsy pour débuter votre utilisation au plus vite.',
                            )
                        }}
                    </p>
                </div>
            </div>
            <div class="advantages-block">
                <div>
                    <CompassIcon icon="download" size="large" />
                </div>
                <div>
                    <h6 class="typographies-heading-h6">
                        {{ $l10n.__('Télécharger nos modèles') }}
                    </h6>
                    <p class="typographies-paragraph-small-regular">
                        {{
                            $l10n.__(
                                'Nous mettons à votre disposition des modèles à télécharger pour vous aider et vous familiariser avec la structure des données dans Sellsy.',
                            )
                        }}
                    </p>
                </div>
            </div>
            <div class="advantages-block">
                <div>
                    <CompassIcon icon="revert" size="large" />
                </div>
                <div>
                    <h6 class="typographies-heading-h6">
                        {{ $l10n.__('Revenez en arrière sous 24h') }}
                    </h6>
                    <p class="typographies-paragraph-small-regular">
                        {{
                            $l10n.__(
                                'Nous sauvegardons vos données régulièrement, revenez en arrière sous 24h pour effacer toutes les erreurs qu’un import aurait pu causer.',
                            )
                        }}
                    </p>
                </div>
            </div>
        </div>
        <h2 id="importAnchor" class="typographies-heading-h2">
            {{ $l10n.__('Historique des imports') }}
        </h2>

        <CompassAlert
            v-if="getImportsList.length === 0"
            class="mb-0"
            type="neutral"
            :description="$t.__('common.labels.no_import_maded')"
            :showClose="false"
        />
        <ImportLogs v-else />
        <!-- IMPORT STEPS + FLATFILE  -->
        <ImportPopup
            v-if="showDialog || getIsRestartImportNeeded"
            :isRestarted="getIsRestartImportNeeded"
            @onClose="onCloseImportDialog"
        />
    </Card>
</template>

<script>
import Card from 'vcomponents/common/card/Card.vue';
import IconImport from 'vcomponents/icons/IconImport.vue';
import ImportPopup from 'vcomponents/settings/import/ImportPopup.vue';
import ImportLogs from 'vcomponents/settings/import/ImportLogs.vue';
import SettingsImportsStore from 'stores/settings/settingsImportsStore';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import { createNamespacedHelpers } from 'vuex';
import Feature from 'Services/Feature';
import Env from 'Services/Env';
const importsStore = createNamespacedHelpers('importsStore');

export default {
    components: { IconImport, ImportPopup, ImportLogs, Preloader, Card },
    data() {
        return {
            importsStore: SettingsImportsStore,
            showDialog: false,
            showDialogDetail: false,
            loading: true,
        };
    },
    computed: {
        ...importsStore.mapGetters([
            'getImportsList',
            'isImportUnlimited',
            'getIsRestartImportNeeded',
            'getMetadataImport',
        ]),
        textAlertLimitedImport() {
            return this.$l10n.__(
                'En période d’essai, l’import est limité à %(lines)s lignes maximum par fichier.',
                { lines: Env.get('flatfile').nonGoldMaxLines },
            );
        },
    },
    beforeCreate() {
        this.$store.registerModule('importsStore', SettingsImportsStore);
    },
    created() {
        this.fetchListOfImports().finally(() => {
            this.checkUrlHash();
        });
        this.updateNonGoldMaxLines(Env.get('flatfile').nonGoldMaxLines);
        if (
            Feature.isEnabled('platform-import-unlimited') ||
            Feature.isEnabled('platform-import-unlimited-whitelist') ||
            Feature.isEnabled('platform-import-unlimited-base')
        ) {
            this.updateImportUnlimited(true);
        }
        this.showDialog = this.getIsRestartImportNeeded;
    },
    beforeUnmount() {
        this.$store.unregisterModule('importsStore');
        window.removeEventListener('hashchange', this.checkUrlHash);
    },
    mounted() {
        window.addEventListener('hashchange', this.checkUrlHash);
    },
    methods: {
        ...importsStore.mapActions([
            'fetchImportsList',
            'updateIsRestartImportNeed',
            'updateImportUnlimited',
            'updateNonGoldMaxLines',
            'updateTypeImport',
            'resetImportList',
        ]),
        onImportCompleted() {
            if (!Feature.isEnabled('import-flatfile-publish-progress')) {
                this.fetchListOfImports().finally(() => {
                    this.scrollIntoViewById();
                });
            } else {
                this.scrollIntoViewById();
            }

            const url = new URL(location.href);
            if (url.search.length > 0) {
                url.search = '';
                history.replaceState(null, document.title, url.toString());
            }
            this.updateIsRestartImportNeed(false);
            this.showDialog = false;
        },
        onCloseImportDialog(isCompleted) {
            this.updateIsRestartImportNeed(false);
            if (isCompleted) {
                this.onImportCompleted();
            }
            this.showDialog = false;
        },
        fetchListOfImports() {
            return new Promise((resolve, reject) => {
                this.loading = true;
                this.resetImportList();
                this.fetchImportsList()
                    .catch(() => {
                        this.loading = false;
                        this.$pushToast({
                            type: 'error',
                            text: this.$l10n.__(
                                "Une erreur est survenue lors de la récupération de l'historique des imports.",
                            ),
                            icon: true,
                        });
                        reject();
                    })
                    .finally(() => {
                        this.loading = false;
                        const params = new Proxy(new URLSearchParams(window.location.search), {
                            get: (searchParams, prop) => searchParams.get(prop),
                        });

                        if (params.importType) {
                            const type = params.importType;
                            this.showDialog = true;
                            const importType = this.getMetadataImport.find(
                                (item) => item.type === type,
                            );
                            if (importType) this.updateTypeImport(importType);
                        }
                        resolve();
                    });
            });
        },
        checkUrlHash() {
            const url = new URL(location.href);
            if (url.hash === '#importAnchor') {
                url.hash = '';
                history.replaceState(null, document.title, url.toString());
                this.scrollIntoViewById();
            }
        },

        scrollIntoViewById() {
            const elem = document.getElementById('importAnchor');
            const jumpTo = parseInt(elem.offsetTop);
            document.getElementById('sellsy_app').scrollTo({
                top: jumpTo - document.getElementsByClassName('navbar')[0].clientHeight - 16,
                behavior: 'smooth',
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.settings-import {
    h1 {
        line-height: 29px;
        margin-bottom: var(--spacing-4);
    }
    h2 {
        line-height: var(--line-heights-24);
        margin-bottom: var(--spacing-3);
        margin-top: 0;
    }
    h4 {
        margin: 0 0 var(--spacing-1) 0;
    }
    p {
        margin-top: 0 !important;
    }

    .documentation-link {
        color: var(--colors-common-text-standard-disabled);
        margin-bottom: var(--spacing-2);
        margin-top: var(--spacing-1);
    }

    .advantages-block-container {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-3);
        margin-bottom: var(--spacing-5);

        .advantages-block {
            display: flex;
            flex-direction: row;
            gap: var(--spacing-1);
            padding: var(--spacing-1);
            div {
                display: flex;
                flex-direction: column;
            }
            h6 {
                line-height: var(--line-heights-16);
                margin: 0;
            }

            p {
                color: var(--colors-common-text-standard-disabled);
                font-size: var(--font-size-12);
                line-height: var(--line-heights-16);
                margin: 0;
            }
        }
    }

    .start-import-block {
        align-items: center;
        background-color: var(--colors-common-background-subtle);
        border-radius: var(--spacing-0);
        display: flex;
        flex-direction: row;
        gap: var(--spacing-3);
        justify-content: space-between;
        margin-bottom: var(--spacing-4);
        padding: var(--spacing-3);

        .information-block {
            align-items: center;
            display: flex;
            flex-direction: row;
            div {
                flex-direction: column;
                margin-left: var(--spacing-3);
                max-width: 590px;
                h3 {
                    line-height: var(--line-heights-24);
                    margin: 0;
                }
                p {
                    margin: 0;
                }
            }
        }
    }
}
.compass-icon:deep() {
    path {
        cursor: initial;
    }
}
</style>
<style lang="scss">
.tooltip-settings-import {
    white-space: pre-line;
}

.settings-import .preloader {
    position: fixed;
    right: 0;
    bottom: 0;
}
</style>
